import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, patch, toastError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { DictateButton } from "@/components/field/DictateButton";
import "@/components/field/field.css";
import { label } from "@/lib/labels";
import { fmtDate, fmtDateTime, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Play, CheckCircle2, MessageSquare, Clock, AlertTriangle, MapPin, Boxes, Camera, Navigation } from "lucide-react";
import { mapsUrl } from "./FieldHome";

export default function FieldTask() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["task", id], queryFn: () => get(`/tasks/${id}`) });
  const [comment, setComment] = useState("");
  const [hours, setHours] = useState("");
  const [confirmDone, setConfirmDone] = useState(false);

  const update = useMutation({
    mutationFn: (body) => patch(`/tasks/${id}`, body),
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: ["task", id] }); qc.invalidateQueries({ queryKey: ["field-today"] }); qc.invalidateQueries({ queryKey: ["field-tasks"] }); qc.invalidateQueries({ queryKey: ["tasks"] });
      if (body.status === "fullfort") toast.success("Oppgave fullført");
      else if (body.status === "pagar") toast.success("Oppgave startet");
      else if (body.comment) { toast.success("Kommentar lagt til"); setComment(""); }
      else if (body.hours_spent) { toast.success("Timer registrert"); setHours(""); }
    },
    onError: (e) => toastError(e, "Kunne ikke oppdatere oppgaven"),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const t = q.data;
  const checklist = t.checklist || [];
  const doneCount = checklist.filter((c) => c.done).length;
  const isDone = ["fullfort", "avbrutt"].includes(t.status);
  const toggle = (cid) => update.mutate({ checklist: checklist.map((c) => (c.id === cid ? { ...c, done: !c.done } : c)) });
  const project = t.project;

  return (
    <div className="space-y-4 fade-up" data-testid="field-task-page">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="field-back-button"><ArrowLeft className="h-4 w-4" /> Tilbake</button>

      <div className="field-card p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5"><StatusBadge status={t.status} /><span className="text-xs text-muted-foreground">{label(t.type)}</span>{t.priority && t.priority !== "normal" && <StatusBadge status={t.priority} />}</div>
        <h1 className="text-[19px] font-display font-semibold leading-snug tracking-tight">{t.title}</h1>
        {t.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t.description}</p>}
        <div className="grid grid-cols-2 gap-2 text-xs pt-1">
          <div><div className="text-muted-foreground">Frist</div><div className="font-medium tabular">{t.due_date ? fmtDate(t.due_date) : "–"}</div></div>
          <div><div className="text-muted-foreground">Timer brukt</div><div className="font-medium tabular">{num(t.hours_spent || 0, 1)} t</div></div>
        </div>
        {project && (
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <Link to={`/felt/prosjekt/${project.id}`} className="min-w-0 flex-1 truncate font-medium" data-testid="field-task-project-link">{project.name}<span className="text-muted-foreground font-normal"> · {project.address}</span></Link>
            <a href={mapsUrl(project.address, project.lat, project.lng)} target="_blank" rel="noreferrer" className="text-primary" aria-label="Naviger"><Navigation className="h-4 w-4" /></a>
          </div>
        )}
      </div>

      {!isDone && (
        <div className="grid grid-cols-2 gap-2">
          {t.status === "ikke_startet" ? (
            <Button className="h-12 rounded-xl" onClick={() => update.mutate({ status: "pagar" })} disabled={update.isPending} data-testid="field-task-start-button"><Play className="h-4 w-4 mr-1.5" /> Start oppgave</Button>
          ) : (
            <Button variant="outline" className="h-12 rounded-xl" onClick={() => update.mutate({ status: "ikke_startet" })} disabled={update.isPending} data-testid="field-task-pause-button">Sett på vent</Button>
          )}
          <Button className="h-12 rounded-xl bg-primary" onClick={() => (doneCount < checklist.length ? setConfirmDone(true) : update.mutate({ status: "fullfort" }))} disabled={update.isPending} data-testid="field-task-complete-button"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Fullfør</Button>
        </div>
      )}

      {checklist.length > 0 && (
        <div className="field-card p-4 space-y-3" data-testid="field-task-checklist">
          <div className="flex items-center justify-between"><h2 className="text-sm font-display font-semibold">Sjekkliste</h2><span className="text-xs text-muted-foreground tabular">{doneCount}/{checklist.length}</span></div>
          <Progress value={checklist.length ? (doneCount / checklist.length) * 100 : 0} className="h-1.5" />
          <ul className="space-y-1">
            {checklist.map((c) => (
              <li key={c.id}>
                <label className={`flex items-start gap-3 rounded-lg px-2 py-2.5 min-h-[44px] cursor-pointer hover:bg-muted/50 ${c.done ? "text-muted-foreground" : ""}`}>
                  <Checkbox checked={!!c.done} onCheckedChange={() => toggle(c.id)} disabled={isDone || update.isPending} className="mt-0.5 h-5 w-5" data-testid={`field-checklist-item-${c.id}`} />
                  <span className={`text-sm ${c.done ? "line-through" : ""}`}>{c.text}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(t.modules || []).length > 0 && (
        <div className="field-card p-4 space-y-2">
          <h2 className="text-sm font-display font-semibold flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> Moduler ({t.modules.length})</h2>
          <div className="flex flex-wrap gap-1.5">{t.modules.map((m) => <span key={m.id} className="rounded-md border bg-muted/40 px-2 py-1 font-mono text-xs" data-testid={`field-task-module-${m.module_id}`}>{m.module_id}</span>)}</div>
        </div>
      )}

      {!isDone && (
        <div className="grid grid-cols-1 gap-3">
          <div className="field-card p-4 space-y-2">
            <h2 className="text-sm font-display font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Registrer timer</h2>
            <div className="flex gap-2">
              <Input type="number" step="0.5" min="0" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Timer" className="h-11" data-testid="field-task-hours-input" />
              <Button className="h-11" variant="outline" disabled={!hours || update.isPending} onClick={() => update.mutate({ hours_spent: Number(hours) })} data-testid="field-task-hours-button">Legg til</Button>
            </div>
          </div>
          <div className="field-card p-4 space-y-2" data-testid="field-task-report">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-display font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Notat / rapport</h2>
              <DictateButton value={comment} onChange={setComment} testId="field-task-comment-dictate" label="Snakk inn rapport" />
            </div>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Status, funn eller beskjed til prosjektleder – snakk inn med mikrofonen eller skriv" className="text-[15px] leading-relaxed rounded-xl" data-testid="field-task-comment-input" />
            <Button className="h-11 w-full rounded-xl" variant="outline" disabled={!comment.trim() || update.isPending} onClick={() => update.mutate({ comment: comment.trim() })} data-testid="field-task-comment-button">Send til loggen</Button>
          </div>
        </div>
      )}

      {(t.comments || []).length > 0 && (
        <div className="field-card p-4 space-y-2" data-testid="field-task-comments">
          <h2 className="text-sm font-display font-semibold">Logg</h2>
          <ul className="space-y-2">{[...t.comments].reverse().map((c, i) => <li key={i} className="text-sm"><div className="text-[11px] text-muted-foreground">{c.user_name || c.by} · {fmtDateTime(c.at || c.created_at)}</div><div>{c.text}</div></li>)}</ul>
        </div>
      )}

      <div className="field-card p-4 space-y-2">
        <h2 className="text-sm font-display font-semibold flex items-center gap-2"><Camera className="h-4 w-4 text-primary" /> Bilder og dokumenter</h2>
        <DocumentsPanel entityType="task" entityId={id} compact defaultCategory="bilde" />
      </div>

      <Button asChild variant="outline" className="w-full h-11 border-amber-300 text-amber-800 hover:bg-amber-50">
        <Link to={`/felt/avvik/ny?project_id=${t.project_id || ""}&task_id=${t.id}${(t.module_ids || [])[0] ? `&module_id=${t.module_ids[0]}` : ""}`} data-testid="field-task-new-issue-button"><AlertTriangle className="h-4 w-4 mr-1.5" /> Registrer avvik på denne oppgaven</Link>
      </Button>

      <AlertDialog open={confirmDone} onOpenChange={setConfirmDone}>
        <AlertDialogContent data-testid="field-task-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fullføre med åpne sjekkpunkter?</AlertDialogTitle>
            <AlertDialogDescription>{checklist.length - doneCount} av {checklist.length} punkter er ikke huket av. Vil du likevel markere oppgaven som fullført?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmDone(false); update.mutate({ status: "fullfort" }); }} data-testid="field-task-confirm-complete-button">Ja, fullfør</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
